import { presetAccess } from '#~/control/preset/access.js';
import { locateProxy } from '#~/agent/index.js';
import type { Device } from '@studio/models/device';
import type { EventHandler } from '@studio/react-utils/event';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { partial } from 'lodash-es';
import { useWiring } from './wiring.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { useListener as useListener_ } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';

/**
 * Command listener.
 *
 * @param deviceId - Device ID.
 */
export const useListener = (deviceId: Device['id']): void => {
  const wiring = useWiring(deviceId);
  const { isProtected } = useAutoTrackingControlProtection(deviceId) ?? {};

  const handleSelectPreset = useLatestFn((index: number) => {
    const nextPreset = wiring?.presets[index];

    if (nextPreset) {
      wiring.selectPreset(nextPreset.id);
    }
  });

  const handleSelectGroup = useLatestFn((offset: -1 | 1) => {
    if (!wiring) {
      return;
    }

    const { groups, selection } = wiring;

    const currentIndex = groups.findIndex((µ) => µ.id === selection.groupId);

    if (currentIndex === -1 || groups.length === 0) {
      return;
    }

    const nextGroup = groups[(currentIndex + offset + groups.length) % groups.length];

    if (nextGroup) {
      wiring.selectGroup(nextGroup.id);
    }
  });

  const jumpTable = useLazyRef<{ [key in Command]?: EventHandler }>(() => ({
    [Command.CONTROL_PRESET_01]: partial(handleSelectPreset, 0),
    [Command.CONTROL_PRESET_02]: partial(handleSelectPreset, 1),
    [Command.CONTROL_PRESET_03]: partial(handleSelectPreset, 2),
    [Command.CONTROL_PRESET_04]: partial(handleSelectPreset, 3),
    [Command.CONTROL_PRESET_05]: partial(handleSelectPreset, 4),
    [Command.CONTROL_PRESET_06]: partial(handleSelectPreset, 5),
    [Command.CONTROL_PRESET_07]: partial(handleSelectPreset, 6),
    [Command.CONTROL_PRESET_08]: partial(handleSelectPreset, 7),
    [Command.CONTROL_PRESET_09]: partial(handleSelectPreset, 8),
    [Command.CONTROL_PRESET_10]: partial(handleSelectPreset, 9)
  })).current;

  useListener_(({ command }) => {
    // Gate only on a loaded 'unsupported' verdict. Calibration is left to the
    // wiring handlers so the hotkey still surfaces the "calibrating" toast.
    if (!wiring || isProtected || presetAccess(locateProxy(deviceId), deviceId) === 'unsupported') {
      return;
    }

    if (command === Command.CONTROL_GROUP_NEXT) {
      handleSelectGroup(1);
    } else if (command === Command.CONTROL_GROUP_PREV) {
      handleSelectGroup(-1);
    } else if (command === Command.CONTROL_PRESET_ADD) {
      wiring.addPreset();
    } else {
      jumpTable[command]?.();
    }
  });
};
